/**
 * useTrackersStore - Hook for tracker management with immediate API sync.
 *
 * All operations are immediately pushed to the server.
 * No local state - the server is the single source of truth.
 */

import { createResourceHook } from "../use-resource-store";
import {
  useTrackers,
  useCreateTracker,
  useUpdateStatusTracker,
  useDeleteTracker,
} from "@/api/tracker";
import { Tracker, TrackerCreate } from "@/models/tracker";

/**
 * Hook for tracker management with immediate API sync.
 *
 * @example
 * ```tsx
 * function TrackerList() {
 *   const {
 *     resources: trackers,
 *     create,
 *     update,
 *     remove,
 *     isLoading,
 *     isUpdating,
 *   } = useTrackersStore();
 *
 *   const handleUpdate = async (id: string, name: string) => {
 *     await update(id, { name });
 *   };
 *
 *   return (
 *     <div>
 *       {trackers.map(t => (
 *         <TrackerItem key={t.id} tracker={t} onUpdate={handleUpdate} />
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export const useTrackersStore = createResourceHook<Tracker, TrackerCreate>({
  resourceName: "tracker",
  resourcePath: "trackers",

  useQueryHook: () => {
    const { data, isLoading, error, refetch } = useTrackers();
    return {
      data: data ?? undefined,
      isLoading,
      error: error as Error | null,
      refetch,
    };
  },

  useCreateMutation: () => {
    const mutation = useCreateTracker();
    return {
      mutateAsync: async (data: TrackerCreate) => {
        const result = await mutation.mutateAsync(data);
        return result as Tracker;
      },
    };
  },

  useUpdateMutation: () => {
    const mutation = useUpdateStatusTracker();
    return {
      mutateAsync: async (
        data: { uriParams: { id: string } } & Record<string, unknown>
      ) => {
        const { uriParams, ...rest } = data;
        const result = await mutation.mutateAsync({
          uriParams: { id: uriParams.id, tracker: rest as unknown as Tracker },
          ...rest,
        } as Parameters<typeof mutation.mutateAsync>[0]);
        return result as Tracker;
      },
    };
  },

  useDeleteMutation: () => {
    const mutation = useDeleteTracker();
    return {
      mutateAsync: async (data: { uriParams: { id: string } }) => {
        await mutation.mutateAsync(data);
      },
    };
  },
});

export default useTrackersStore;
